import { awsConfig } from '../config/aws';

const API_BASE = awsConfig.api.endpoint;

async function getAuthToken(): Promise<string> {
  // Get token from Amplify session
  const { fetchAuthSession } = await import('aws-amplify/auth');
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() || '';
}

async function apiRequest(path: string, options: RequestInit = {}): Promise<any> {
  const token = await getAuthToken();

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...options.headers,
    },
  });

  if (!response.ok) {
    let detail = '';
    try { detail = (await response.json())?.error || ''; } catch {}
    throw new Error(detail || `API error: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

export const api = {
  // Recordings
  getRecordings: (params?: { systemId?: string; date?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.systemId) query.set('systemId', params.systemId);
    if (params?.date) query.set('date', params.date);
    if (params?.limit) query.set('limit', params.limit.toString());
    return apiRequest(`/recordings?${query}`);
  },

  getSystemRecordings: (systemId: string, params?: { date?: string }) => {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    return apiRequest(`/systems/${systemId}/recordings?${query}`);
  },

  // Search (legacy — client-side pagination)
  search: (params: {
    query?: string;
    systemId?: string;
    dateFrom?: string;
    dateTo?: string;
    agent?: string;
  }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    return apiRequest(`/search?${query}`);
  },

  // Paginated search (server-side pagination)
  searchPaginated: (params: {
    systemId?: string;
    dateFrom: string;
    dateTo: string;
    campaign?: string;
    agent?: string;
    direction?: string;
    hideShortCalls?: string;
    phone?: string;
    pageSize?: number;
    nextKey?: string;
    skipCount?: string;
  }) => {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query.set(key, String(value));
      }
    });
    return apiRequest(`/search?${query}`);
  },

  // Get filter options (campaigns, agents) for a system
  getSearchFilters: (params: { systemId?: string }) => {
    const query = new URLSearchParams({ action: 'search-filters' });
    if (params.systemId) query.set('systemId', params.systemId);
    return apiRequest(`/stats/daily?${query}`);
  },

  // Stats
  getStats: (params?: { systemId?: string; date?: string }) => {
    const query = new URLSearchParams();
    if (params?.systemId) query.set('systemId', params.systemId);
    if (params?.date) query.set('date', params.date);
    return apiRequest(`/stats?${query}`);
  },

  getDailyStats: (params?: { date?: string; systemId?: string }) => {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    if (params?.systemId) query.set('systemId', params.systemId);
    return apiRequest(`/stats/daily?${query}`);
  },

  // Systems / SFTP setup
  getSystems: () => apiRequest('/sftp/setup'),

  getSystemSetup: (systemId: string) => apiRequest(`/sftp/setup?systemId=${systemId}`),

  // Playback - get pre-signed URL for audio streaming
  getPlaybackUrl: (recordingKey: string) =>
    apiRequest(`/playback?key=${encodeURIComponent(recordingKey)}`),

  // Transcription
  startTranscription: (recordingKey: string) =>
    apiRequest('/transcribe/start', {
      method: 'POST',
      body: JSON.stringify({ recordingKey }),
    }),

  getTranscriptResult: async (recordingKey: string) => {
    try {
      return await apiRequest(`/transcribe/result?key=${encodeURIComponent(recordingKey)}`);
    } catch {
      return { status: 'NOT_FOUND' };
    }
  },

  getTranscriptionStatus: async (recordingKey: string) => {
    try {
      return await apiRequest(`/transcribe/status?key=${encodeURIComponent(recordingKey)}`);
    } catch {
      return { status: 'NOT_FOUND' };
    }
  },

  // PDF download
  getTranscriptPdfUrl: (recordingKey: string) =>
    `${API_BASE}/pdf?key=${encodeURIComponent(recordingKey)}`,

  // Access control
  getWhitelist: (systemId?: string) => {
    const query = new URLSearchParams();
    if (systemId) query.set('systemId', systemId);
    return apiRequest(`/access/whitelist?${query}`);
  },

  addWhitelist: (systemId: string, ip: string, label?: string) =>
    apiRequest('/access/whitelist', {
      method: 'POST',
      body: JSON.stringify({ systemId, ip, label }),
    }),

  removeWhitelist: (systemId: string, ip: string) =>
    apiRequest('/access/whitelist', {
      method: 'DELETE',
      body: JSON.stringify({ systemId, ip }),
    }),

  getAccessLog: (params?: { date?: string; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    if (params?.status) query.set('status', params.status);
    return apiRequest(`/access/log?${query}`);
  },

  approveAccess: (ip: string, systemId: string, label?: string) =>
    apiRequest('/access/approve', {
      method: 'POST',
      body: JSON.stringify({ ip, systemId, label }),
    }),

  // User management
  listUsers: () => apiRequest('/users'),

  createUser: (email: string, role: string = 'viewer') =>
    apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify({ email, role }),
    }),

  deleteUser: (username: string) =>
    apiRequest('/users', {
      method: 'DELETE',
      body: JSON.stringify({ username }),
    }),

  resetUserPassword: (username: string) =>
    apiRequest('/users/reset-password', {
      method: 'POST',
      body: JSON.stringify({ username }),
    }),

  changeUserRole: (username: string, role: 'admin' | 'viewer') =>
    apiRequest('/users/role', {
      method: 'POST',
      body: JSON.stringify({ username, role }),
    }),

  setUserCapability: (username: string, capability: string, enabled: boolean) =>
    apiRequest('/users/capability', {
      method: 'POST',
      body: JSON.stringify({ username, capability, enabled }),
    }),

  // AWS cost summary (Dashboard collapsible panel)
  getCostSummary: (period: 7 | 30 | 90 | 365) =>
    apiRequest(`/stats/daily?action=cost-summary&period=${period}`),

  // Download failures (Quarantine tab — GHL ghosted records)
  getDownloadFailures: () =>
    apiRequest(`/stats/daily?action=download-failures`),

  // No-Recording records (terminal — GHL confirmed no audio exists, retries exhausted)
  getNoRecordings: () =>
    apiRequest(`/stats/daily?action=no-recordings`),

  // Missing-PIT records (location sending webhooks with no PIT in the SSM map)
  getMissingPit: () =>
    apiRequest(`/stats/daily?action=missing-pit`),

  // GHL Locations admin page
  getGhlLocations: () =>
    apiRequest(`/stats/daily?action=ghl-locations`),

  ghlMute: (locationId: string) =>
    apiRequest(`/stats/daily?action=ghl-mute&locationId=${encodeURIComponent(locationId)}`),

  ghlUnmute: (locationId: string) =>
    apiRequest(`/stats/daily?action=ghl-unmute&locationId=${encodeURIComponent(locationId)}`),

  // PIT travels in a header, never the URL (query strings can end up in logs).
  ghlOnboard: (locationId: string, pit: string) =>
    apiRequest(`/stats/daily?action=ghl-onboard&locationId=${encodeURIComponent(locationId)}`, {
      headers: { 'X-Ghl-Pit': pit },
    }),

  ghlRename: (locationId: string, name: string) =>
    apiRequest(`/stats/daily?action=ghl-rename&locationId=${encodeURIComponent(locationId)}&name=${encodeURIComponent(name)}`),

  // Missed calls (inbound voicemail / no-answer — nobody available)
  getMissedCalls: () =>
    apiRequest(`/stats/daily?action=missed-calls`),

  retryDownload: (PK: string, SK: string) =>
    apiRequest(`/stats/daily?action=retry-download&PK=${encodeURIComponent(PK)}&SK=${encodeURIComponent(SK)}`),

  deleteFailedRecording: (PK: string, SK: string) =>
    apiRequest(`/stats/daily?action=delete-recording&PK=${encodeURIComponent(PK)}&SK=${encodeURIComponent(SK)}`),

  // Upload failures (Quarantine tab — UploadCalls failures)
  getUploadFailures: () =>
    apiRequest(`/stats/daily?action=upload-failures`),

  reportUploadFailure: (payload: {
    PK: string;
    SK: string;
    stage: 'ingest' | 'converting' | 'uploading' | 'confirming' | 'unknown';
    error: string;
    fileSizeBytes?: number;
    fileName?: string;
  }) =>
    apiRequest('/upload', {
      method: 'POST',
      body: JSON.stringify({ action: 'report-failure', ...payload }),
    }),

  getUploadProgress: (items: Array<{ PK: string; SK: string }>) =>
    apiRequest('/upload', {
      method: 'POST',
      body: JSON.stringify({ action: 'progress', items }),
    }),

  // Audit log
  getAuditLog: (date?: string) => {
    const query = new URLSearchParams({ action: 'audit-log' });
    if (date) query.set('date', date);
    return apiRequest(`/stats/daily?${query}`);
  },

  // CSV export
  exportCsv: (params?: { systemId?: string; dateFrom?: string; dateTo?: string }) => {
    const query = new URLSearchParams({ action: 'csv-export' });
    if (params?.systemId) query.set('systemId', params.systemId);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return apiRequest(`/stats/daily?${query}`);
  },

  // Operations status
  getOpsStatus: () => apiRequest('/stats/daily?action=ops-status'),

  // Glacier restore
  requestGlacierRestore: (s3Key: string, recordingId?: string) => {
    const query = new URLSearchParams({ action: 'glacier-restore', key: s3Key });
    if (recordingId) query.set('recordingId', recordingId);
    return apiRequest(`/stats/daily?${query}`);
  },

  // Flagged files (incomplete metadata)
  getFlaggedFiles: (systemId?: string) => {
    const query = new URLSearchParams({ action: 'flagged-files' });
    if (systemId) query.set('systemId', systemId);
    return apiRequest(`/stats/daily?${query}`);
  },

  // Manually attribute an agent to a flagged row (after listening to ID them).
  // Sets agentId AND removes attentionStatus, so the row drops out of the
  // Flagged tab. Admin-only.
  attributeAgent: (PK: string, SK: string, agentId: string) => {
    const query = new URLSearchParams({
      action: 'attribute-agent',
      PK,
      SK,
      agentId,
    });
    return apiRequest(`/stats/daily?${query}`);
  },

  // Storage Monitor (Dashboard panel) — admin-only.
  // Returns NAS disk usage + per-bucket per-storage-class size + monthly cost.
  getStorageStatus: () => apiRequest('/stats/daily?action=storage-status'),

  // Export formats (multiplexed through GET /stats/daily)
  listExportFormats: () => apiRequest('/stats/daily?action=list-formats'),

  saveExportFormat: (name: string, pattern: string, id?: string) => {
    const query = new URLSearchParams({ action: 'save-format', name, pattern });
    if (id) query.set('id', id);
    return apiRequest(`/stats/daily?${query}`);
  },

  deleteExportFormat: (id: string) =>
    apiRequest(`/stats/daily?action=delete-format&id=${encodeURIComponent(id)}`),

  // Quarantine
  getQuarantine: (params?: { systemId?: string; dateFrom?: string; dateTo?: string }) => {
    const query = new URLSearchParams();
    if (params?.systemId) query.set('systemId', params.systemId);
    if (params?.dateFrom) query.set('dateFrom', params.dateFrom);
    if (params?.dateTo) query.set('dateTo', params.dateTo);
    return apiRequest(`/quarantine?${query}`);
  },

  // Health
  getHealth: () => apiRequest('/health'),
  // Rides /health with a view param — a dedicated /nas-status API GW resource
  // exceeded the api Lambda's resource-policy size limit (2026-07-16).
  getNasStatus: () => apiRequest('/health?view=nas-status'),

  // API keys
  listApiKeys: () => apiRequest('/api-keys'),

  createApiKey: (tenantId: string, label: string, systemId?: string) =>
    apiRequest('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ tenantId, label, systemId }),
    }),

  deleteApiKey: (key: string, tenantId: string) =>
    apiRequest('/api-keys', {
      method: 'DELETE',
      body: JSON.stringify({ key, tenantId }),
    }),

  regenerateApiKey: (key: string, tenantId: string) =>
    apiRequest('/api-keys', {
      method: 'POST',
      body: JSON.stringify({ action: 'regenerate', key, tenantId }),
    }),

  // Blocked IP alerts
  getBlockedIps: () => apiRequest('/stats/daily?action=blocked-ips'),
  dismissBlockedIp: (ip: string) => apiRequest(`/stats/daily?action=dismiss-blocked-ip&ip=${encodeURIComponent(ip)}`),

  // Systems management
  listSystems: () => apiRequest('/stats/daily?action=list-systems'),

  createSystem: (systemId: string, label: string) =>
    apiRequest(`/stats/daily?action=create-system&systemId=${encodeURIComponent(systemId)}&label=${encodeURIComponent(label)}`),

  // Sync
  triggerSync: (systemId: string, entries: any[]) =>
    apiRequest('/sync', {
      method: 'POST',
      body: JSON.stringify({ systemId, entries }),
    }),

  // Bulk download
  startBulkDownload: (recordingIds: string[], downloadType: 'calls' | 'calls-and-transcripts', filenamePattern?: string, groupByDate?: boolean) => {
    const query = new URLSearchParams({
      action: 'bulk-download-start',
      recordingIds: JSON.stringify(recordingIds),
      downloadType,
    });
    if (filenamePattern) query.set('filenamePattern', filenamePattern);
    if (groupByDate) query.set('groupByDate', 'true');
    return apiRequest(`/stats/daily?${query}`);
  },

  checkBulkDownload: (jobId: string) =>
    apiRequest(`/stats/daily?action=bulk-download-status&jobId=${encodeURIComponent(jobId)}`),

  // Upload calls
  uploadIngestBatch: (items: Array<{
    confirmationNumber: string;
    phone: string;
    date: string;
    time: string;
    agentId: string;
    agentName: string;
    agentEmail: string;
    beneficiaryName: string;
    audioContentType?: string;
    systemId?: string;
  }>) =>
    apiRequest('/upload', {
      method: 'POST',
      body: JSON.stringify({ action: 'ingest-batch', items }),
    }),

  uploadConfirmBatch: (items: Array<{
    recordingId: string;
    s3Key: string;
    PK: string;
    SK: string;
    duration?: number;
    tempUploadKey?: string | null;
    sourceContentType?: string;
  }>) =>
    apiRequest('/upload', {
      method: 'POST',
      body: JSON.stringify({ action: 'confirm-batch', items }),
    }),

  // Agent aliases
  getAgentAliases: () => apiRequest('/stats/daily?action=agent-aliases'),

  getAgentAliasMap: () => apiRequest('/stats/daily?action=agent-alias-map'),

  createAgentAlias: (aliasValue: string, canonicalName: string, aliasType: string = 'other') => {
    const query = new URLSearchParams({ action: 'agent-alias-create', aliasValue, canonicalName, aliasType });
    return apiRequest(`/stats/daily?${query}`);
  },

  deleteAgentAlias: (aliasValue: string) =>
    apiRequest(`/stats/daily?action=agent-alias-delete&aliasValue=${encodeURIComponent(aliasValue)}`),

  // SMS/ConverselyAI export
  startSmsExport: (items: Array<{ PK: string; SK: string; s3Key: string; csvData: Record<string, string>; callIdSuffix?: string }>, dryRun?: boolean) =>
    apiRequest('/upload', {
      method: 'POST',
      body: JSON.stringify({ action: 'sms-export', items, dryRun }),
    }),

  checkSmsExport: (jobId: string) =>
    apiRequest(`/stats/daily?action=sms-export-status&jobId=${encodeURIComponent(jobId)}`),
};
